<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-6 gap-3">
      <h3 class="text-h5 font-weight-bold white--text mr-auto">Schulungen &amp; Termine</h3>

      <v-btn-toggle v-model="filter" mandatory dense rounded background-color="transparent" dark>
        <v-btn small value="upcoming"><v-icon left small>mdi-calendar-arrow-right</v-icon>Bevorstehend</v-btn>
        <v-btn small value="past"><v-icon left small>mdi-history</v-icon>Vergangen</v-btn>
        <v-btn small value="all"><v-icon left small>mdi-all-inclusive</v-icon>Alle</v-btn>
      </v-btn-toggle>

      <v-btn color="#D4AF37" class="black--text font-weight-bold" @click="openDialog()" rounded> <v-icon left>mdi-plus</v-icon> Neuer Termin </v-btn>
    </div>

    <v-row v-if="loading">
      <v-col v-for="n in 3" :key="n" cols="12" md="6" lg="4">
        <v-skeleton-loader type="card" dark></v-skeleton-loader>
      </v-col>
    </v-row>

    <v-row v-else-if="filteredTermine.length">
      <v-col v-for="t in filteredTermine" :key="t._id" cols="12" md="6" lg="4">
        <v-card class="termin-tile rounded-xl overflow-hidden h-100" dark elevation="0" @click="openDialog(t)">
          <div class="tile-ribbon" :class="phaseClass(t)">{{ phase(t) }}</div>

          <div class="tile-header px-4 pt-4 pb-3">
            <div class="d-flex align-center">
              <div class="date-badge mr-3">
                <div class="date-month">{{ monthShort(t.startsAt) }}</div>
                <div class="date-day">{{ day(t.startsAt) }}</div>
              </div>
              <div style="min-width: 0; flex: 1">
                <h3 class="text-h6 font-weight-bold text-truncate">{{ t.title }}</h3>
                <div class="text-caption grey--text d-flex align-center">
                  <v-icon small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                  {{ time(t.startsAt) }} – {{ time(t.endsAt) }}
                </div>
              </div>
            </div>
          </div>

          <v-divider class="border-white-05"></v-divider>

          <v-card-text class="pa-4">
            <div class="d-flex align-center text-caption grey--text mb-2">
              <v-icon small color="#D4AF37" class="mr-1">mdi-map-marker</v-icon>
              <span class="text-truncate">{{ t.location || "Ort offen" }}</span>
            </div>
            <p class="description-clip text-body-2 grey--text text--lighten-1 mb-3">{{ t.description || "—" }}</p>

            <div class="d-flex align-center mb-2">
              <div class="text-caption grey--text mr-2">Plätze</div>
              <v-progress-linear
                :value="((t.registeredCount || 0) / Math.max(1, t.maxParticipants)) * 100"
                color="#D4AF37"
                height="6"
                rounded
                background-color="rgba(255,255,255,0.08)"
                class="flex-grow-1"></v-progress-linear>
              <span class="ml-2 text-caption gold-text font-weight-bold">{{ t.registeredCount || 0 }}/{{ t.maxParticipants }}</span>
            </div>
          </v-card-text>

          <v-divider class="border-white-05"></v-divider>

          <div class="d-flex align-center pa-2">
            <v-btn icon small color="blue" @click.stop="openDialog(t)"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn icon small color="grey" @click.stop="openRegistrations(t)"><v-icon small>mdi-account-multiple-outline</v-icon></v-btn>
            <v-spacer />
            <v-btn icon small color="red" @click.stop="confirmDelete(t)"><v-icon small>mdi-delete</v-icon></v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <div v-else class="empty-state text-center pa-8 rounded-lg grey--text">
      <v-icon size="56" color="grey darken-2">mdi-calendar-blank</v-icon>
      <h3 class="text-h6 mt-3">Keine Termine in dieser Ansicht.</h3>
    </div>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="dialog" max-width="820" persistent dark scrollable>
      <v-card style="background-color: #1e1e1e; max-height: 90vh">
        <v-card-title>
          <span class="text-h5 gold-text">{{ editingId ? "Termin bearbeiten" : "Neuer Termin" }}</span>
          <v-spacer />
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text style="max-height: 70vh">
          <v-form ref="form" v-model="valid">
            <v-text-field v-model="form.title" label="Titel" :rules="[(v) => !!v || 'Titel erforderlich']" required outlined dense></v-text-field>
            <v-textarea v-model="form.description" label="Beschreibung" outlined rows="2" dense></v-textarea>

            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.startsAt" label="Start" type="datetime-local" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.endsAt" label="Ende" type="datetime-local" outlined dense></v-text-field>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.location" label="Ort" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="form.maxParticipants" label="Max. Teilnehmer" type="number" outlined dense></v-text-field>
              </v-col>
            </v-row>

            <v-select
              v-model="form.requiredModules"
              :items="allModules"
              item-text="title"
              item-value="_id"
              label="Pflichtmodule (Voraussetzung)"
              multiple
              chips
              outlined
              dense></v-select>

            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.cancellationDeadlineHours"
                  label="Storno-Frist (h vor Beginn)"
                  type="number"
                  outlined
                  dense></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center">
                <v-switch v-model="form.isRefresher" color="#D4AF37" label="Auffrischungskurs" hide-details></v-switch>
              </v-col>
            </v-row>

            <div class="mt-4 mb-2 gold-text text-h6">Anmeldefenster</div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.registrationStart" label="Anmeldung ab" type="datetime-local" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.registrationEnd" label="Anmeldung bis" type="datetime-local" outlined dense></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialog = false">Abbrechen</v-btn>
          <v-btn color="#D4AF37" class="black--text" @click="saveTermin" :disabled="!valid">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Registrations Dialog -->
    <v-dialog v-model="regDialog" max-width="820" dark scrollable>
      <v-card style="background-color: #1e1e1e">
        <v-card-title class="d-flex align-center flex-wrap gap-2">
          <v-icon left color="#D4AF37">mdi-account-multiple</v-icon>
          <span>Teilnehmer</span>
          <span class="grey--text text-truncate">· {{ activeTitle }}</span>
          <v-spacer />
          <v-chip x-small color="green" text-color="white" class="mr-1">{{ countByStatus("registered") }} angemeldet</v-chip>
          <v-chip x-small color="orange" text-color="white" class="mr-1">{{ countByStatus("waiting_list") }} Warteliste</v-chip>
          <v-chip x-small color="blue" text-color="white" class="mr-1">{{ countByStatus("attended") }} besucht</v-chip>
          <v-btn icon @click="regDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text style="max-height: 70vh">
          <div class="d-flex align-center mb-3 flex-wrap gap-2">
            <v-btn-toggle v-model="regFilter" dense mandatory rounded background-color="transparent" dark>
              <v-btn small value="all">Alle</v-btn>
              <v-btn small value="active">Aktiv</v-btn>
              <v-btn small value="cancelled">Storno</v-btn>
            </v-btn-toggle>
            <v-spacer />
            <v-btn small outlined color="#D4AF37" rounded @click="exportExcel" :loading="exportingExcel">
              <v-icon left small>mdi-file-excel</v-icon> Excel
            </v-btn>
          </div>

          <div v-if="!filteredRegistrations.length" class="text-center grey--text pa-6">
            <v-icon size="48" color="grey darken-2">mdi-account-off-outline</v-icon>
            <div class="mt-2">Keine Einträge in dieser Ansicht.</div>
          </div>

          <div v-for="r in filteredRegistrations" :key="r._id" class="reg-row d-flex align-center pa-3 rounded-lg mb-2">
            <v-avatar size="40" color="grey darken-3" class="mr-3">
              <span class="text-caption white--text">{{ initials(r.user) }}</span>
            </v-avatar>
            <div class="flex-grow-1" style="min-width: 0">
              <div class="font-weight-bold">{{ r.user ? r.user.vorname + " " + r.user.nachname : "-" }}</div>
              <div class="text-caption grey--text">
                {{ r.user && r.user.studio }}
                <template v-if="r.registeredAt"> · seit {{ formatShort(r.registeredAt) }}</template>
              </div>
            </div>
            <v-chip x-small :color="statusColor(r.status)" text-color="white" class="mr-2">{{ statusLabel(r.status) }}</v-chip>

            <!-- Actions -->
            <div class="d-flex align-center reg-actions">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="blue"
                    :disabled="r.status === 'attended' || actionBusy[r._id]"
                    :loading="actionBusy[r._id] === 'attended'"
                    @click="markAttended(r, true)">
                    <v-icon small>mdi-check-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Als anwesend markieren</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="grey lighten-1"
                    :disabled="r.status === 'no_show' || actionBusy[r._id]"
                    :loading="actionBusy[r._id] === 'no_show'"
                    @click="markAttended(r, false)">
                    <v-icon small>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Nicht erschienen</span>
              </v-tooltip>
              <v-tooltip top v-if="r.status === 'attended' || r.status === 'no_show'">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" color="grey" :loading="actionBusy[r._id] === 'reset'" @click="resetAttendance(r)">
                    <v-icon small>mdi-restore</v-icon>
                  </v-btn>
                </template>
                <span>Zurücksetzen</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="red"
                    :disabled="r.status === 'cancelled' || actionBusy[r._id]"
                    :loading="actionBusy[r._id] === 'cancel'"
                    @click="adminCancel(r)">
                    <v-icon small>mdi-account-remove-outline</v-icon>
                  </v-btn>
                </template>
                <span>Abmelden</span>
              </v-tooltip>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top right>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "TerminManager",
  data() {
    return {
      termine: [],
      allModules: [],
      loading: false,
      dialog: false,
      regDialog: false,
      registrations: [],
      activeTermin: null,
      activeTitle: "",
      regFilter: "active",
      actionBusy: {},
      exportingExcel: false,
      valid: false,
      editingId: null,
      filter: "upcoming",
      form: this.emptyForm(),
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    filteredTermine() {
      const now = new Date()
      const list = [...(this.termine || [])].sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
      if (this.filter === "upcoming") return list.filter((t) => new Date(t.startsAt) >= now)
      if (this.filter === "past") return list.filter((t) => new Date(t.startsAt) < now).reverse()
      return list
    },
    filteredRegistrations() {
      if (!this.registrations) return []
      if (this.regFilter === "all") return this.registrations
      if (this.regFilter === "cancelled") return this.registrations.filter((r) => r.status === "cancelled")
      return this.registrations.filter((r) => r.status !== "cancelled")
    },
  },
  mounted() {
    this.loadTermine()
    this.loadModules()
  },
  methods: {
    emptyForm() {
      const iso = new Date().toISOString().substring(0, 16)
      return {
        title: "",
        description: "",
        startsAt: iso,
        endsAt: iso,
        location: "",
        maxParticipants: 10,
        requiredModules: [],
        cancellationDeadlineHours: 72,
        isRefresher: false,
        registrationStart: iso,
        registrationEnd: iso,
      }
    },
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    async loadTermine() {
      this.loading = true
      try {
        this.termine = (await api.akademie.fetchTermine()) || []
      } catch (e) {
        this.notify("error", "Termine konnten nicht geladen werden")
      } finally {
        this.loading = false
      }
    },
    async loadModules() {
      try {
        this.allModules = (await api.akademie.fetchModules()) || []
      } catch (e) {
        /* ignore */
      }
    },
    monthShort(d) {
      if (!d) return ""
      return new Date(d).toLocaleDateString("de-CH", { month: "short" }).replace(".", "")
    },
    day(d) {
      if (!d) return ""
      return new Date(d).getDate()
    },
    time(d) {
      if (!d) return ""
      return new Date(d).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    initials(u) {
      if (!u) return "?"
      return ((u.vorname || "?")[0] + (u.nachname ? u.nachname[0] : "")).toUpperCase()
    },
    phase(t) {
      if (!t.registrationStart || !t.registrationEnd) return "—"
      const now = new Date()
      if (now < new Date(t.registrationStart)) return "Geplant"
      if (now > new Date(t.registrationEnd)) return "Geschlossen"
      return "Geöffnet"
    },
    phaseClass(t) {
      const p = this.phase(t)
      if (p === "Geöffnet") return "ribbon-success"
      if (p === "Geplant") return "ribbon-warning"
      return "ribbon-error"
    },
    statusColor(s) {
      if (s === "registered") return "green"
      if (s === "waiting_list") return "orange"
      if (s === "cancelled") return "grey"
      if (s === "attended") return "blue"
      return "grey"
    },
    openDialog(item = null) {
      if (item) {
        this.editingId = item._id
        // Formatierung für <v-text-field type="datetime-local">
        // .format() ohne Parameter gibt lokales ISO-8601 zurück
        this.form = {
          title: item.title || "",
          description: item.description || "",
          startsAt: item.startsAt ? this.$moment(item.startsAt).format("YYYY-MM-DDTHH:mm") : "",
          endsAt: item.endsAt ? this.$moment(item.endsAt).format("YYYY-MM-DDTHH:mm") : "",
          location: item.location || "",
          maxParticipants: item.maxParticipants || 10,
          requiredModules: item.requiredModules ? item.requiredModules.map((m) => m._id || m) : [],
          cancellationDeadlineHours: item.cancellationDeadlineHours || 72,
          isRefresher: !!item.isRefresher,
          registrationStart: item.registrationStart ? this.$moment(item.registrationStart).format("YYYY-MM-DDTHH:mm") : "",
          registrationEnd: item.registrationEnd ? this.$moment(item.registrationEnd).format("YYYY-MM-DDTHH:mm") : "",
        }
      } else {
        this.editingId = null
        this.form = this.emptyForm()
      }
      this.dialog = true
    },
    async saveTermin() {
      if (!this.$refs.form.validate()) return

      // Wir erstellen eine Kopie der Form-Daten für den Versand
      const payload = { ...this.form }

      // Wir konvertieren die lokalen Strings explizit via Moment
      // Damit wird das korrekte Date-Objekt erzeugt, das beim
      // JSON-Stringify den richtigen UTC-Offset mitgibt.
      payload.startsAt = this.$moment(this.form.startsAt).toDate()
      payload.endsAt = this.$moment(this.form.endsAt).toDate()
      payload.registrationStart = this.$moment(this.form.registrationStart).toDate()
      payload.registrationEnd = this.$moment(this.form.registrationEnd).toDate()

      try {
        if (this.editingId) {
          await api.akademie.updateTermin(this.editingId, payload)
          this.notify("success", "Termin aktualisiert")
        } else {
          await api.akademie.createTermin(payload)
          this.notify("success", "Termin erstellt")
        }
        this.dialog = false
        this.loadTermine()
      } catch (e) {
        this.notify("error", "Speichern fehlgeschlagen")
      }
    },
    async confirmDelete(item) {
      if (!confirm(`Termin "${item.title}" wirklich löschen?`)) return
      try {
        await api.akademie.deleteTermin(item._id)
        this.loadTermine()
        this.notify("success", "Termin gelöscht")
      } catch (e) {
        this.notify("error", "Löschen fehlgeschlagen")
      }
    },
    async openRegistrations(t) {
      this.regDialog = true
      this.activeTitle = t.title
      this.activeTermin = t
      this.registrations = []
      this.actionBusy = {}
      try {
        this.registrations = (await api.akademie.fetchRegistrations(t._id)) || []
      } catch (e) {
        this.notify("error", "Anmeldungen konnten nicht geladen werden")
      }
    },
    countByStatus(status) {
      return (this.registrations || []).filter((r) => r.status === status).length
    },
    statusLabel(s) {
      const map = { registered: "Angemeldet", waiting_list: "Warteliste", cancelled: "Storno", attended: "Besucht", no_show: "Nicht da" }
      return map[s] || s
    },
    formatShort(d) {
      if (!d) return ""
      return new Date(d).toLocaleDateString("de-CH")
    },
    setBusy(id, action) {
      this.$set(this.actionBusy, id, action)
    },
    clearBusy(id) {
      this.$set(this.actionBusy, id, null)
    },
    async markAttended(r, attended) {
      if (!this.activeTermin) return
      this.setBusy(r._id, attended ? "attended" : "no_show")
      try {
        const updated = await api.akademie.markAttendance(this.activeTermin._id, r._id, attended)
        Object.assign(r, updated)
        this.notify("success", attended ? "Als anwesend markiert" : "Als nicht erschienen markiert")
      } catch (e) {
        this.notify("error", "Markieren fehlgeschlagen")
      } finally {
        this.clearBusy(r._id)
      }
    },
    async resetAttendance(r) {
      this.setBusy(r._id, "reset")
      try {
        const updated = await api.akademie.markAttendance(this.activeTermin._id, r._id, null)
        Object.assign(r, updated)
      } catch (e) {
        this.notify("error", "Zurücksetzen fehlgeschlagen")
      } finally {
        this.clearBusy(r._id)
      }
    },
    async adminCancel(r) {
      if (!confirm(`Anmeldung von ${r.user ? r.user.vorname + " " + r.user.nachname : "?"} stornieren?`)) return
      this.setBusy(r._id, "cancel")
      try {
        const updated = await api.akademie.adminCancelRegistration(this.activeTermin._id, r._id)
        Object.assign(r, updated)
        this.notify("success", "Anmeldung storniert")
        // Liste neu laden, weil ggf. jemand von der Warteliste nachrückt
        this.registrations = (await api.akademie.fetchRegistrations(this.activeTermin._id)) || []
        this.loadTermine()
      } catch (e) {
        this.notify("error", "Stornieren fehlgeschlagen")
      } finally {
        this.clearBusy(r._id)
      }
    },
    async exportExcel() {
      if (!this.activeTermin) return
      this.exportingExcel = true
      try {
        await api.akademie.exportTerminExcel(this.activeTermin._id, this.activeTermin.title)
        this.notify("success", "Export wurde gestartet")
      } catch (e) {
        this.notify("error", "Excel-Export fehlgeschlagen")
      } finally {
        this.exportingExcel = false
      }
    },
  },
}
</script>

<style scoped>
.gold-text {
  background: linear-gradient(45deg, #d4af37, #ffd700);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.gap-3 {
  gap: 12px;
}
.h-100 {
  height: 100%;
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
.description-clip {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.termin-tile {
  background: rgba(30, 30, 30, 0.85) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
  cursor: pointer;
}
.termin-tile:hover {
  transform: translateY(-3px);
  border-color: rgba(212, 175, 55, 0.4);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.5);
}

.tile-ribbon {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 10px;
  font-size: 0.7rem;
  font-weight: bold;
  border-radius: 99px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  backdrop-filter: blur(6px);
  background: rgba(0, 0, 0, 0.5);
}
.ribbon-success {
  color: #66bb6a;
  border: 1px solid #66bb6a;
}
.ribbon-warning {
  color: #ffb74d;
  border: 1px solid #ffb74d;
}
.ribbon-error {
  color: #ef5350;
  border: 1px solid #ef5350;
}

.date-badge {
  width: 56px;
  text-align: center;
  background: rgba(212, 175, 55, 0.12);
  border: 1px solid rgba(212, 175, 55, 0.25);
  border-radius: 10px;
  padding: 6px 0;
  flex-shrink: 0;
}
.date-month {
  font-size: 0.7rem;
  text-transform: uppercase;
  color: #d4af37;
  letter-spacing: 0.5px;
}
.date-day {
  font-size: 1.4rem;
  font-weight: bold;
  color: white;
  line-height: 1.1;
}

.reg-row {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.empty-state {
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.08);
}

.reg-actions {
  flex-shrink: 0;
}

.gap-2 {
  gap: 8px;
}
</style>
